/**
 * PHONE INPUT COMPONENT
 * Composant d'entrée téléphone avec sélecteur d'indicatif pays
 */

import { forwardRef, useState, useCallback } from 'react';
import type { InputHTMLAttributes, ChangeEvent } from 'react';
import { cn } from '../../lib/utils';
import { ChevronDown } from 'lucide-react';

/**
 * Liste des indicatifs pays disponibles
 * Triés par pertinence pour le contexte Desertours (France en premier, puis pays francophones/désert)
 */
export const COUNTRY_CODES = [
  { code: 'FR', dial: '+33', name: 'France', flag: '🇫🇷' },
  { code: 'BE', dial: '+32', name: 'Belgique', flag: '🇧🇪' },
  { code: 'CH', dial: '+41', name: 'Suisse', flag: '🇨🇭' },
  { code: 'LU', dial: '+352', name: 'Luxembourg', flag: '🇱🇺' },
  { code: 'MC', dial: '+377', name: 'Monaco', flag: '🇲🇨' },
  { code: 'ES', dial: '+34', name: 'Espagne', flag: '🇪🇸' },
  { code: 'MA', dial: '+212', name: 'Maroc', flag: '🇲🇦' },
  { code: 'PT', dial: '+351', name: 'Portugal', flag: '🇵🇹' },
  { code: 'IT', dial: '+39', name: 'Italie', flag: '🇮🇹' },
  { code: 'DE', dial: '+49', name: 'Allemagne', flag: '🇩🇪' },
  { code: 'GB', dial: '+44', name: 'Royaume-Uni', flag: '🇬🇧' },
  { code: 'NL', dial: '+31', name: 'Pays-Bas', flag: '🇳🇱' },
  { code: 'HU', dial: '+36', name: 'Hongrie', flag: '🇭🇺' },
  { code: 'AT', dial: '+43', name: 'Autriche', flag: '🇦🇹' },
  { code: 'DZ', dial: '+213', name: 'Algérie', flag: '🇩🇿' },
  { code: 'CA', dial: '+1', name: 'Canada', flag: '🇨🇦' },
  { code: 'US', dial: '+1', name: 'États-Unis', flag: '🇺🇸' },
  { code: 'EG', dial: '+20', name: 'Égypte', flag: '🇪🇬' },
  { code: 'TN', dial: '+216', name: 'Tunisie', flag: '🇹🇳' },
  { code: 'SN', dial: '+221', name: 'Sénégal', flag: '🇸🇳' },
  { code: 'CI', dial: '+225', name: "Côte d'Ivoire", flag: '🇨🇮' },
  { code: 'ML', dial: '+223', name: 'Mali', flag: '🇲🇱' },
  { code: 'NE', dial: '+227', name: 'Niger', flag: '🇳🇪' },
  { code: 'MR', dial: '+222', name: 'Mauritanie', flag: '🇲🇷' },
  { code: 'SA', dial: '+966', name: 'Arabie saoudite', flag: '🇸🇦' },
  { code: 'AE', dial: '+971', name: 'Émirats arabes unis', flag: '🇦🇪' },
  { code: 'RS', dial: '+381', name: 'Serbie', flag: '🇷🇸' },
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number]['code'];

/**
 * Extraire l'indicatif et le numéro local d'un numéro complet
 */
export function parsePhoneNumber(fullNumber: string): {
  countryCode: CountryCode;
  localNumber: string;
} {
  if (!fullNumber) {
    return { countryCode: 'FR', localNumber: '' };
  }

  const trimmed = fullNumber.trim();

  // Chercher un indicatif correspondant (du plus long au plus court)
  const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.dial.length - a.dial.length);

  for (const country of sortedCodes) {
    if (trimmed.startsWith(country.dial)) {
      const localNumber = trimmed.slice(country.dial.length).trim();
      return { countryCode: country.code, localNumber };
    }
  }

  // Si le numéro commence par 0, c'est probablement un numéro français sans indicatif
  if (trimmed.startsWith('0')) {
    return { countryCode: 'FR', localNumber: trimmed };
  }

  // Par défaut, retourner le numéro tel quel avec FR
  return { countryCode: 'FR', localNumber: trimmed };
}

/**
 * Formater un numéro complet avec l'indicatif
 */
export function formatPhoneNumber(countryCode: CountryCode, localNumber: string): string {
  if (!localNumber) return '';

  const country = COUNTRY_CODES.find((c) => c.code === countryCode);
  if (!country) return localNumber;

  // Nettoyer le numéro local (retirer les espaces, tirets, etc.)
  let cleaned = localNumber.replace(/[\s\-().]/g, '');

  // Si le numéro commence par 0, le retirer pour les indicatifs non-US
  if (cleaned.startsWith('0') && country.dial !== '+1') {
    cleaned = cleaned.slice(1);
  }

  return `${country.dial}${cleaned}`;
}

export interface PhoneInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  /** Valeur complète du téléphone (avec indicatif) */
  value?: string;
  /** Callback quand la valeur change (retourne le numéro complet avec indicatif) */
  onChange?: (value: string) => void;
  /** Code pays par défaut */
  defaultCountryCode?: CountryCode;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, value = '', onChange, defaultCountryCode = 'FR', disabled, ...props }, ref) => {
    // Parser la valeur initiale
    const parsed = parsePhoneNumber(value);
    const [countryCode, setCountryCode] = useState<CountryCode>(
      value ? parsed.countryCode : defaultCountryCode
    );
    const [localNumber, setLocalNumber] = useState(parsed.localNumber);

    // Mettre à jour quand la valeur externe change
    const externalParsed = parsePhoneNumber(value);
    if (
      value &&
      (externalParsed.countryCode !== countryCode || externalParsed.localNumber !== localNumber)
    ) {
      // Sync uniquement si la valeur vient de l'extérieur
      if (formatPhoneNumber(countryCode, localNumber) !== value) {
        setCountryCode(externalParsed.countryCode);
        setLocalNumber(externalParsed.localNumber);
      }
    }

    const handleCountryChange = useCallback(
      (e: ChangeEvent<HTMLSelectElement>) => {
        const newCode = e.target.value as CountryCode;
        setCountryCode(newCode);
        onChange?.(formatPhoneNumber(newCode, localNumber));
      },
      [localNumber, onChange]
    );

    const handleNumberChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const newNumber = e.target.value;
        setLocalNumber(newNumber);
        onChange?.(formatPhoneNumber(countryCode, newNumber));
      },
      [countryCode, onChange]
    );

    return (
      <div className={cn('flex w-full min-w-0', className)}>
        {/* Sélecteur de pays */}
        <div className="relative flex-shrink-0">
          <select
            value={countryCode}
            onChange={handleCountryChange}
            disabled={disabled}
            className={cn(
              'h-9 appearance-none rounded-l-md border border-r-0 border-zinc-200 bg-zinc-50 pl-3 pr-8 text-sm outline-none transition-colors',
              'focus-visible:border-zinc-900 focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:z-10',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'cursor-pointer hover:bg-zinc-100'
            )}
          >
            {COUNTRY_CODES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.dial}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
        </div>

        {/* Input numéro */}
        <input
          ref={ref}
          type="tel"
          value={localNumber}
          onChange={handleNumberChange}
          disabled={disabled}
          placeholder={props.placeholder || '6 12 34 56 78'}
          className={cn(
            'h-9 min-w-0 flex-1 rounded-r-md border border-zinc-200 bg-white px-3 text-sm outline-none transition-colors',
            'placeholder:text-zinc-400',
            'focus-visible:border-zinc-900 focus-visible:ring-2 focus-visible:ring-zinc-900',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
          {...props}
        />
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';
